import { useInfiniteQuery } from '@tanstack/react-query';
import type { ColumnDef } from '@tanstack/react-table';
import { listPurgeRuns } from '@/api/ops';
import type { MaintenanceOverview, PurgeRun } from '@/api/opsTypes';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { DataTable } from '@/ui/DataTable';
import { KeysetMore } from '@/ui/KeysetMore';
import { QueryError } from '@/ui/QueryError';

const when = (iso: string | null) => (iso ? new Date(iso).toLocaleString() : '—');

const runColumns: ColumnDef<PurgeRun, never>[] = [
  { accessorKey: 'job', header: 'Job' },
  { id: 'started', header: 'Started', cell: ({ row }) => when(row.original.started_at) },
  { id: 'cutoff', header: 'Purged older than', cell: ({ row }) => when(row.original.cutoff) },
  { accessorKey: 'deleted_count', header: 'Deleted', meta: { numeric: true } },
  {
    id: 'result',
    header: 'Result',
    cell: ({ row }) =>
      row.original.succeeded ? (
        <Badge variant="secondary">Succeeded</Badge>
      ) : (
        <span className="flex flex-col gap-1">
          <Badge variant="destructive">Failed</Badge>
          <span className="text-xs text-muted-foreground">{row.original.error}</span>
        </span>
      ),
  },
];

export function RetentionSection({ overview }: { overview: MaintenanceOverview }) {
  const runs = useInfiniteQuery({
    queryKey: ['maintenance', 'purge-runs'],
    queryFn: ({ pageParam }) => listPurgeRuns(undefined, pageParam),
    initialPageParam: undefined as { after: string; after_id: string } | undefined,
    getNextPageParam: (last) => last.next ?? undefined,
  });
  const lastRunByJob = new Map(overview.last_purge_runs.map((run) => [run.job, run]));

  return (
    <Card>
      <CardHeader>
        <CardTitle>Retention</CardTitle>
        <CardDescription>
          What is kept, for how long, and the latest purge of each job.
        </CardDescription>
      </CardHeader>
      <CardContent className="flex flex-col gap-4">
        <ul className="flex flex-col gap-2 text-sm">
          {overview.retention.map((rule) => {
            const last = lastRunByJob.get(rule.store);
            return (
              <li key={rule.store} className="rounded-lg border p-3">
                <div className="font-medium">
                  {rule.label}{' '}
                  <span className="font-normal text-muted-foreground">kept {rule.kept_for}</span>
                </div>
                <div className="text-muted-foreground">Cutoff {when(rule.cutoff)}</div>
                <div className="text-muted-foreground">{rule.job}</div>
                {last && (
                  <div>
                    Last run {when(last.started_at)} — {last.succeeded ? 'succeeded' : 'failed'}
                  </div>
                )}
              </li>
            );
          })}
        </ul>

        <h3 className="text-sm font-medium">Purge runs</h3>
        {runs.isError ? (
          <QueryError error={runs.error} onRetry={() => void runs.refetch()} />
        ) : (
          <>
            <DataTable
              columns={runColumns}
              data={runs.data?.pages.flatMap((page) => page.items) ?? []}
              getRowId={(row) => row.id}
              isLoading={runs.isPending}
              empty="No purge has run yet."
            />
            <KeysetMore
              hasMore={runs.hasNextPage}
              isFetching={runs.isFetchingNextPage}
              onMore={() => void runs.fetchNextPage()}
            />
          </>
        )}
      </CardContent>
    </Card>
  );
}
